import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ILoginResponse } from '@interfaces/auth';
import { BehaviorSubject, Observable, Subject, tap, throwError } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { EXPIRES_AT_KEY, REFRESH_TOKEN_NAME, STORAGE_KEYS, TOKEN_NAME } from '@constants/global';
import { Router } from '@angular/router';
import { ToastService } from './toast.service';
import { environment } from '../../../environments/environment';
import { BreadcrumbService } from './breadcrumb.service';
import { UserStateService } from './user-state.service';
import { VaultService } from './vault.service';
import { QueryCacheService } from '@components/d-form/services/query-cache.service';
import { sanitizeReturnUrl } from '../guards/return-url.util';
import { InactivityService } from './inactivity.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private http = inject(HttpClient);
	private cookieService = inject(CookieService);
	private router = inject(Router);
	private messageService = inject(ToastService);
	private breadcrumbService = inject(BreadcrumbService);
	private userStateService = inject(UserStateService);
	private vaultService = inject(VaultService);
	private queryCacheService = inject(QueryCacheService);
	private inactivityService = inject(InactivityService);

	isRefreshing = new BehaviorSubject<boolean>(false);
	refreshTokenSubject = new Subject<string | null>();

	private refreshTokenTimer: any;

	constructor() {
		this.initTokenRefreshOnLoad();

		// Si ya hay sesión activa al cargar la app (refresh de página),
		// retomamos el monitoreo de inactividad.
		if (this.getTokens().accessToken) {
			this.startInactivityTracking();
		}

		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				this.checkAndRefreshIfNeeded();
			}
		});
	}

	private startInactivityTracking(): void {
		this.inactivityService.start(() => {
			// Al timeout, usamos tu flujo existente: limpia todo,
			// redirige a /login y muestra el toast "Session Expired".
			this.removeTokens();
		});
	}

	private checkAndRefreshIfNeeded() {
		const expiresAt = localStorage.getItem(EXPIRES_AT_KEY);
		if (expiresAt) {
			this.scheduleTokenRefresh(parseInt(expiresAt, 10));
		}
	}

	/**
 * Lee returnUrl de la URL actual (o de sessionStorage si venimos de OAuth)
 * y navega a la ruta destino. Usar después de cualquier autenticación exitosa.
 */
	navigateAfterAuth(): void {
		// 1. Prioridad: sessionStorage (caso OAuth, donde se perdieron los query params)
		const stored = sessionStorage.getItem('returnUrl');
		if (stored) {
			sessionStorage.removeItem('returnUrl');
			this.router.navigateByUrl(sanitizeReturnUrl(stored));
			return;
		}

		// 2. Leer de la URL actual (caso login normal, MFA, TOTP)
		const tree = this.router.parseUrl(this.router.url);
		const raw = tree.queryParamMap.get('returnUrl');
		this.router.navigateByUrl(sanitizeReturnUrl(raw));
	}

	/* Verify MFA code (APP) and complete authentication */
	verifyMfaTotp(username: string, session: string, code: string, challengeName: 'SOFTWARE_TOKEN_MFA' | 'MFA_SETUP', refreshToken?: string): Observable<any> {
		const body: any = { username, session, code, challengeName };
		if (refreshToken) {
			body.refreshToken = refreshToken;
		}
		return this.http.post<any>(`${environment.baseService}/auth/totp/mfa/verify`, body);
	}

	/* Setup MFA TOTP (Generate QR code) - For mandatory MFA during first login */
	setUpMfaTotp(session: string, username: string, refreshToken: string): Observable<any> {
		const body: any = { session, username, refreshToken };
		return this.http.post<any>(`${environment.baseService}/auth/mfa/setup`, body);
	}

	/* Verify MFA code during authentication */
	verifyMfa(email: string, session: string, mfaCode: string): Observable<any> {
		const body: any = { email, session, mfaCode };
		return this.http.post<any>(`${environment.baseService}/auth/email/verify-mfa`, body);
	}

	/* Refresh access and ID tokens using a valid refresh token */
	refreshToken(): Observable<any> {
		const { refreshToken } = this.getTokens();
		if (!refreshToken) {
			return throwError(() => new Error());
		}
		const body = { refreshToken };
		return this.http.post<any>(`${environment.baseService}/auth/refresh-token`, body).pipe(
			tap((response) => {
				this.saveTokens(response.data);
			})
		);
	}

	/* Logout user by invalidating the access token */
	logout(): Observable<any> {
		const { accessToken } = this.getTokens();
		const headers = new HttpHeaders({
			Authorization: `Bearer ${accessToken}`,
		});

		return this.http.post<any>(`${environment.baseService}/auth/logout`, {}, { headers });
	}

	/* Authenticate user with SRP flow */
	login(email: string, password: string): Observable<ILoginResponse> {
		const body: any = { email, password };

		this.queryCacheService.clear();
		localStorage.clear();

		return this.http.post<ILoginResponse>(`${environment.baseService}/auth/login`, body);
	}

	/* Initiate forgot password flow - send reset code to email */
	forgotPassword(email: string): Observable<any> {
		const headers = new HttpHeaders({
			email,
		});
		return this.http.post<any>(
			`${environment.baseService}/auth/forgot-password`,
			{},
			{ headers }
		);
	}

	authCallBack(code: string): Observable<any> {
		const headers = new HttpHeaders({
			uuid: code,
			// redirectUrl,
		});
		return this.http.post<any>(
			`${environment.baseService}/auth/oauth/callback`,
			{},
			{ headers }
		);
	}

	/* Confirm forgot password with reset user code and new password */
	confirmForgotPassword(email: string, confirmationCode: string, newPassword: string): Observable<any> {
		const body: any = { email, confirmationCode, newPassword };
		return this.http.post<any>(`${environment.baseService}/auth/confirm-forgot-password`, body);
	}

	/* Change password during login challenge (e.g. after temporary password) */
	changePassword(email: string, session: string, newPassword: string): Observable<any> {
		const body: any = { email, session, newPassword };
		return this.http.post<any>(`${environment.baseService}/auth/change-password`, body);
	}

	saveTokens(data: any, logIn: boolean = false) {
		const { refreshToken, accessToken, expiresAt } = data || {};
		if (!accessToken || !expiresAt) {
			return;
		}
		const expiresDate = new Date(parseInt(expiresAt, 10));

		localStorage.setItem(EXPIRES_AT_KEY, expiresAt);

		this.scheduleTokenRefresh(expiresAt);
		/* const { refreshToken, accessToken } = data || {};
		if (!accessToken) {
			return;
		}
		const tenMinutesMs = 6 * 60 * 1000;
		const expiresAt = String(Date.now() + tenMinutesMs); // formato: "1760050192000"

		const expiresDate = new Date(parseInt(expiresAt, 10));

		localStorage.setItem(EXPIRES_AT_KEY, expiresAt);

		this.scheduleTokenRefresh(Number(expiresAt)); */


		this.breadcrumbService.clear()
		this.cookieService.set(TOKEN_NAME, accessToken, {
			path: '/',
			secure: true,
			sameSite: 'Lax',
			expires: expiresDate,
		});
		if (refreshToken) {
			this.cookieService.set(REFRESH_TOKEN_NAME, refreshToken, {
				path: '/',
				secure: true,
				sameSite: 'Lax',
				expires: expiresDate,
			});
		}
		this.startInactivityTracking();

		if (logIn) {
			this.navigateAfterAuth();
		}
	}

	getTokens(): { refreshToken: string; accessToken: string } {
		const accessToken: string = this.cookieService.get(TOKEN_NAME);
		const refreshToken: string = this.cookieService.get(REFRESH_TOKEN_NAME);
		return { accessToken, refreshToken };
	}

	removeTokens(isLogOut?: boolean): void {
		this.inactivityService.stop();

		this.breadcrumbService.clear()
		this.cookieService.delete(TOKEN_NAME, '/');
		this.userStateService.clearUserState();
		this.cookieService.delete(REFRESH_TOKEN_NAME, '/');
		this.vaultService.remove(STORAGE_KEYS.RECENT_SEARCHES);

		localStorage.clear();

		this.router.navigate(['/login']);
		if (!isLogOut) {
			this.messageService.show({
				type: 'warn',
				title: 'Session Expired',
				detail: 'Your session has expired. Please sign in again.',
				lifetime: 10000,
			});
		} else {
			this.messageService.show({
				type: 'info',
				title: 'Signed Out',
				detail: 'You have securely signed out of your session.',
				lifetime: 10000,
			});
		}

		if (this.refreshTokenTimer) {
			clearTimeout(this.refreshTokenTimer);
		}
	}

	private initTokenRefreshOnLoad() {
		const expiresAt = localStorage.getItem(EXPIRES_AT_KEY);
		if (expiresAt) {
			this.scheduleTokenRefresh(parseInt(expiresAt, 10));
		}
	}

	private scheduleTokenRefresh(expirationTimestamp: number) {
		if (this.refreshTokenTimer) {
			clearTimeout(this.refreshTokenTimer);
		}

		const now = new Date().getTime();
		const refreshMargin = 300000; // 5 minutos
		// Cuánto falta para llegar al momento de refrescar (no al de expirar)
		const timeUntilRefresh = expirationTimestamp - refreshMargin - now;

		if (timeUntilRefresh > 0) {
			// Caso normal: Aún falta para entrar en la zona de refresh
			this.refreshTokenTimer = setTimeout(() => {
				this.refreshToken().subscribe({
					error: () => this.removeTokens(),
				});
			}, timeUntilRefresh);
		} else {
			// Si timeUntilRefresh es negativo, significa que ya estamos 
			// dentro de los últimos 5 minutos de vida del token (o ya expiró).
			// Debemos intentar refrescar INMEDIATAMENTE.

			// Verificamos si el token no ha expirado totalmente (damos un margen de gracia de 1 seg)
			if ((expirationTimestamp - now) > 0) {
				this.refreshToken().subscribe({
					error: () => this.removeTokens(),
				});
			} else {
				// Si ya expiró totalmente, logout
				this.removeTokens();
			}
		}
	}

	isTokenExpired(): boolean {
		const expiresAt = localStorage.getItem(EXPIRES_AT_KEY);
		if (!expiresAt) {
			return true;
		}

		const expirationTime = parseInt(expiresAt, 10);
		const now = new Date().getTime();
		const timeRemaining = expirationTime - now;

		const shouldRefresh = timeRemaining < 300000;

		return shouldRefresh;
	}
}
